/**
 * The resolution queue, out as a sheet and back as a decision (X-09 Stage 3).
 *
 * **Why a workbook and not a screen.** 7,878 products on one real client still
 * need a code, and the tail is flat rather than Pareto — the top 500 are half
 * the queue's turnover and most of the rest carry under ₹10,000. Per-product
 * clicking is not a job anyone finishes, and this practice already works in
 * Excel.
 *
 * **The upload is the largest write the product can make**, so the copy says
 * what it will do before it does it, and the service refuses a file with one
 * bad code rather than applying half of it.
 */
import { Alert, Button, Card } from '@fiveox/ui'
import { BookOpen, Download, FileSpreadsheet, Upload, Wrench } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { DOCUMENT, useEvaluation } from '../lib/access'
import { ApiError, type HsnRepair, type HsnSeed, recon, review } from '../lib/api'
import { saveFile } from '../lib/files'
import { repairSummary } from '../lib/hsnRepair'
import { seedSummary } from '../lib/hsnSeed'

interface Applied {
  products: number
  rows: number
  documents: number
  skipped_unchanged: number
  demoted_periods: string[]
}

export function HsnQueueCard({
  clientId,
  gstin,
  period,
  onApplied,
}: {
  clientId: string
  gstin: string
  /**
   * The month to export. Absent when no document is selected, and the
   * reconciliation button is then not offered: a workbook needs a month, and
   * guessing one would export somebody else's figures under this heading.
   */
  period?: string | null
  onApplied?: () => void
}) {
  const { can } = useEvaluation()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState<Applied | null>(null)
  const [repaired, setRepaired] = useState<HsnRepair | null>(null)
  const [seeded, setSeeded] = useState<HsnSeed | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  const save = useCallback(async () => {
    setBusy('download')
    setError(null)
    try {
      const blob = await review.hsnQueue(clientId)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `hsn-codes-${gstin}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The queue could not be built.')
    } finally {
      setBusy(null)
    }
  }, [clientId, gstin])

  /**
   * The month's reconciliation, as the six-sheet workbook the practice reads.
   *
   * Beside the HSN queue rather than on the Reconciliation screen because this
   * is where a reviewer already is when they want the month in a file — and
   * the two downloads answer the same instinct, so they sit together.
   */
  const saveRecon = useCallback(async () => {
    if (!period) return
    setBusy('recon')
    setError(null)
    try {
      // Saved under the service's name (D-127). The fallback is for a response that sent none.
      saveFile(await recon.exportWorkbook(clientId, period), `GSTR-2B Reconciliation - ${period}.xlsx`)
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'The reconciliation could not be built.',
      )
    } finally {
      setBusy(null)
    }
  }, [clientId, period])

  /**
   * Finding A4 (stage 5c). The dropped-zero repair — `4051000` stored where
   * `04051000` was written, and now and then a trailing zero a register's own
   * export lost — has exactly one answer, so the owner ruled it
   * needs nobody to choose (2026-09-14). It had a route and no button, while
   * 109 such codes held 10,818 rows out of one real August's Table 12.
   */
  const repair = useCallback(async () => {
    setBusy('repair')
    setError(null)
    setRepaired(null)
    try {
      setRepaired((await review.repairHsn(clientId)).data)
      onApplied?.()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The repair could not be run.')
    } finally {
      setBusy(null)
    }
  }, [clientId, onApplied])

  /**
   * Finding A5 (stage 5d). What this client's own registers consistently say
   * about a product's code, remembered — so the queue stops asking about it and
   * a short heading elsewhere is PROPOSED the full code. Proposed, never
   * applied; the owner's ruling of 2026-09-13 lifted the old refusal, and the
   * seed still skips anything it cannot stand behind.
   */
  const seed = useCallback(async () => {
    setBusy('seed')
    setError(null)
    setSeeded(null)
    try {
      setSeeded((await review.seedHsn(clientId)).data)
      onApplied?.()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The registers could not be read.')
    } finally {
      setBusy(null)
    }
  }, [clientId, onApplied])

  const send = useCallback(
    async (file: File) => {
      setBusy('upload')
      setError(null)
      setApplied(null)
      try {
        setApplied((await review.applyHsnQueue(clientId, file)).data)
        onApplied?.()
      } catch (err) {
        // A failure here means NOTHING was written — the service validates
        // every code before it applies any of them. Say so, because the
        // natural fear on a bulk write is that half of it landed.
        setError(
          err instanceof ApiError
            ? `${err.message} Nothing was applied.`
            : 'That file could not be applied. Nothing was changed.',
        )
      } finally {
        setBusy(null)
        if (picker.current) picker.current.value = ''
      }
    },
    [clientId, onApplied],
  )

  return (
    <Card className="mb-4 p-4">
      <p className="gst-panel-h mb-2">HSN codes to confirm</p>
      <p className="gst-meta mb-3">
        Download the products still needing a code, biggest turnover first, with
        the directory&rsquo;s own descriptions beside them. Fill the{' '}
        <strong>HSN to use</strong> column and send it back — a blank row is
        left for later, and one code the directory does not publish refuses the
        whole file rather than applying half of it. Codes that only lost a zero on
        the way into a spreadsheet can be restored first, without the sheet.
      </p>

      {error && (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      )}

      {seeded && (
        <Alert tone="info" className="mb-3">
          {seedSummary(seeded)}
        </Alert>
      )}

      {repaired && (
        <Alert tone="info" className="mb-3">
          {repairSummary(repaired)}
        </Alert>
      )}

      {applied && (
        <Alert tone="info" className="mb-3">
          Applied <strong>{applied.products.toLocaleString()}</strong> product
          {applied.products === 1 ? '' : 's'} across{' '}
          <strong>{applied.rows.toLocaleString()}</strong> row
          {applied.rows === 1 ? '' : 's'} in {applied.documents} document
          {applied.documents === 1 ? '' : 's'}.
          {applied.skipped_unchanged > 0 && (
            <>
              {' '}
              {applied.skipped_unchanged.toLocaleString()} row
              {applied.skipped_unchanged === 1 ? '' : 's'} already carried the
              answer and {applied.skipped_unchanged === 1 ? 'was' : 'were'} left
              alone.
            </>
          )}
          {applied.demoted_periods.length > 0 && (
            <>
              {' '}
              <strong>
                {applied.demoted_periods.length} period
                {applied.demoted_periods.length === 1 ? '' : 's'} went back to
                review
              </strong>{' '}
              because this changed data they had already sent.
            </>
          )}
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" disabled={busy !== null} onClick={() => void save()}>
          <Download size={12} /> {busy === 'download' ? 'Building…' : 'Download the queue'}
        </Button>

        {/*
          * The month against the 2B, as a workbook. Only when there IS a month
          * — the export needs one, and a file headed with the wrong period is
          * worse than no file.
          */}
        {period && (
          <Button
            type="button"
            onClick={() => void saveRecon()}
            disabled={busy !== null}
            variant="outline"
            size="sm"
          >
            <FileSpreadsheet size={12} />{' '}
            {busy === 'recon' ? 'Building…' : 'Download the reconciliation'}
          </Button>
        )}

        {can(DOCUMENT, 'can_update') && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy !== null}
              onClick={() => void repair()}
              title="Codes that lost a zero on the way into a spreadsheet — usually the leading one (4051000 for 04051000), sometimes a trailing one. Only where exactly one completion is a code in the directory; a code a person chose is never changed."
            >
              <Wrench size={12} /> {busy === 'repair' ? 'Restoring…' : 'Restore dropped zeros'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy !== null}
              onClick={() => void seed()}
              title="Remember the code each product carries consistently in this client's own registers. Proposed on short headings, never applied; products coded two ways, headings, and a person's decisions are left alone."
            >
              <BookOpen size={12} /> {busy === 'seed' ? 'Reading…' : 'Learn from the registers'}
            </Button>
            <input
              ref={picker}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void send(file)
              }}
            />
            <Button
              type="button"
              size="sm"
              disabled={busy !== null}
              onClick={() => picker.current?.click()}
            >
              <Upload size={12} /> {busy === 'upload' ? 'Applying…' : 'Send the filled sheet back'}
            </Button>
          </>
        )}
      </div>
    </Card>
  )
}
